import { MessageSquare } from "lucide-react";
import type { RecentChat } from "@racler/protocol";
import { PaginationFooter } from "./PaginationFooter.tsx";
import { relativeTime } from "../lib/chatGroups.ts";
import { paginate } from "../lib/homeData.ts";
import { navigate, projectPath } from "../lib/router.ts";

interface Props {
  allChats: RecentChat[];
  filteredChats: RecentChat[];
  projectNames: Map<string, string>;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function RecentChatsSection({
  allChats,
  filteredChats,
  projectNames,
  page,
  pageSize,
  onPageChange,
}: Props) {
  const paged = paginate(filteredChats, page, pageSize);

  return (
    <section className="home-section" aria-label="Recent Chats">
      <h2 className="home-section-title">Recent Chats</h2>
      <div className="home-card" data-testid="recent-chats-card">
        {allChats.length === 0 ? (
          <div className="home-empty-state" data-testid="empty-chats">
            <p className="home-empty-title">No recent chats</p>
            <p className="home-empty-desc muted small">
              Start a chat in any project to see it here.
            </p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="home-empty-state" data-testid="empty-search-chats">
            <p className="home-empty-title">No matching chats</p>
            <p className="home-empty-desc muted small">
              Try searching with different terms.
            </p>
          </div>
        ) : (
          <>
            <ul className="home-card-list">
              {paged.pageItems.map((c) => {
                const projectName =
                  c.projectName ||
                  projectNames.get(`${c.machineId}:${c.projectId}`) ||
                  c.projectId;

                return (
                  <li key={c.sessionId} className="home-row-wrap">
                    <button
                      className="home-list-row"
                      onClick={() =>
                        navigate(projectPath(c.machineId, c.projectId, c.sessionId))
                      }
                      data-testid="recent-chat"
                      title={`Open chat: ${c.title}`}
                    >
                      <span className="home-row-icon">
                        <MessageSquare size={14} aria-hidden />
                      </span>
                      <span className="home-row-main">
                        <span className="home-row-title-line">
                          <span className="home-row-title">{c.title}</span>
                        </span>
                        <span className="home-row-subline">
                          <span className="home-row-subline-item">{projectName}</span>
                        </span>
                      </span>
                      <span className="home-row-meta">
                        <span className="home-row-time">{relativeTime(c.updatedAt)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <PaginationFooter
              page={page}
              total={filteredChats.length}
              pageSize={pageSize}
              onPageChange={onPageChange}
              entityName="Chats"
            />
          </>
        )}
      </div>
    </section>
  );
}
